import React, { useEffect, useMemo, useState } from "react";
import api, { eur } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

// Commande saisie par l'equipe pour un client (telephone, visite en magasin). Le serveur l'enregistre
// directement comme « Reçue », sauf s'il reste des articles « sur devis » a chiffrer.
export function NewOrderDialog({ onCreated }) {
  const { sauces } = useBrand();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [clientId, setClientId] = useState("");
  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [lines, setLines] = useState({});          // productId -> { quantity, sauce }
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setClientQuery(""); setClientId(""); setProducts([]); setProductQuery(""); setLines({}); setNotes("");
  };

  useEffect(() => {
    if (open) api.get("/clients").then((r) => setClients(r.data)).catch(() => toast.error("Clients introuvables"));
  }, [open]);

  // Les prix dependent du client (tarifs negocies) : on recharge le catalogue a chaque changement de client.
  useEffect(() => {
    if (!clientId) { setProducts([]); return; }
    api.get("/products", { params: { client_id: clientId } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [clientId]);

  const client = clients.find((c) => c.id === clientId);
  const shownClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    const list = q
      ? clients.filter((c) => [c.name, c.enseigne, c.city, c.email].some((v) => (v || "").toLowerCase().includes(q)))
      : clients;
    return list.slice(0, 30);
  }, [clients, clientQuery]);
  const shownProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    return q ? products.filter((p) => [p.name, p.code, p.category].some((v) => (v || "").toLowerCase().includes(q))) : products;
  }, [products, productQuery]);

  const setQty = (p, value) => {
    const q = Math.max(0, parseInt(value, 10) || 0);
    setLines((l) => {
      if (!q) { const { [p.id]: _, ...rest } = l; return rest; }
      return { ...l, [p.id]: { quantity: q, sauce: l[p.id]?.sauce || sauces[0] || "" } };
    });
  };
  const setLineSauce = (p, sauce) => setLines((l) => (l[p.id] ? { ...l, [p.id]: { ...l[p.id], sauce } } : l));

  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  const chosen = Object.entries(lines).filter(([id]) => byId[id]);
  const total = chosen.reduce((s, [id, l]) => s + (byId[id].price_on_request ? 0 : byId[id].price * l.quantity), 0);
  const hasQuote = chosen.some(([id]) => byId[id].price_on_request);

  const submit = async () => {
    if (!clientId || !chosen.length) return;
    setBusy(true);
    try {
      const items = chosen.map(([id, l]) => ({ product_id: id, quantity: l.quantity, sauce: l.sauce || "" }));
      const { data } = await api.post("/orders", { client_id: clientId, items, notes });
      toast.success(`Commande ${data.order_number} enregistrée pour ${client?.name}`, {
        description: data.quote ? "Des articles sont sur devis : chiffrez-les avant de valider." : "Le client reçoit la confirmation par email.",
      });
      setOpen(false);
      reset();
      onCreated && (await onCreated());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer la commande");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="new-order-btn"><Plus className="mr-1.5 h-4 w-4" /> Nouvelle commande</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouvelle commande pour un client</DialogTitle>
          <DialogDescription>Pour une commande reçue par téléphone ou prise sur place. Le client reçoit la confirmation par email.</DialogDescription>
        </DialogHeader>

        {!client ? (
          <div className="space-y-2">
            <Label htmlFor="new-order-client-search">Client</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="new-order-client-search" className="pl-9" autoFocus value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)} placeholder="Nom, ville ou email du client..." data-testid="new-order-client-search" />
            </div>
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {shownClients.map((c) => (
                <button key={c.id} type="button" onClick={() => setClientId(c.id)} data-testid={`new-order-client-${c.id}`}
                  className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm hover:border-primary hover:bg-primary/5">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{[c.enseigne, c.city].filter(Boolean).join(" · ") || c.email}</span>
                </button>
              ))}
              {shownClients.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Aucun client trouvé.</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md bg-primary/5 px-3 py-2">
              <div>
                <p className="text-sm font-semibold">{client.name}</p>
                <p className="text-xs text-muted-foreground">{[client.enseigne, client.city, client.email].filter(Boolean).join(" · ")}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setClientId(""); setLines({}); }}>Changer</Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={productQuery} onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Rechercher un produit..." data-testid="new-order-product-search" />
            </div>
            <div className="max-h-80 space-y-1 overflow-y-auto">
              {shownProducts.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.unit} · {p.price_on_request ? "sur devis" : eur(p.price)}</p>
                  </div>
                  {sauces.length > 0 && lines[p.id] && (
                    <Select value={lines[p.id].sauce} onValueChange={(v) => setLineSauce(p, v)}>
                      <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{sauces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Input type="number" min="0" inputMode="numeric" className="h-8 w-20 text-right" aria-label={`Quantité ${p.name}`}
                    value={lines[p.id]?.quantity || ""} placeholder="0" onChange={(e) => setQty(p, e.target.value)} data-testid={`new-order-qty-${p.id}`} />
                </div>
              ))}
              {shownProducts.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Aucun produit.</p>}
            </div>

            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Note (facultatif)" data-testid="new-order-notes" />
          </div>
        )}

        <DialogFooter className="items-center gap-3 sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {chosen.length} produit(s) · <span className="tabular font-semibold text-foreground">{eur(total)}</span>
            {hasQuote && " + articles sur devis"}
          </p>
          <Button onClick={submit} disabled={busy || !client || !chosen.length} data-testid="new-order-submit">
            {busy ? "Enregistrement..." : "Enregistrer la commande"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
